import { NetworkStatus } from '@apollo/client'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import {
  GetEmployeeResultsSummariesDocument,
  GetEmployeeResultsSummariesQuery,
  ResultConceptItemFragment,
} from 'generated/resources/graphql'

import { useEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'

import { usePayrollResults } from 'modules/payroll_bundle/lib/PayrollResultContext'
import {
  INITIAL_PAGE,
  PAGINATION_PER_PAGE,
  UPDATE_DATA,
} from 'modules/payroll_bundle/lib/constants'

import { useConcepts } from './useConcepts'

export type PayrollEmployeeResultsSummary =
  GetEmployeeResultsSummariesQuery['payroll']['employeeResultsSummariesConnection']['nodes'][number]
export type PayrollResultConceptItem = ResultConceptItemFragment

export interface PayrollResultColumnConfig {
  id: number
  name: string
  label: string
  isVisible: boolean
  default: boolean
}

/**
 * Replacement of useResultConceptItems
 */
export const useEmployeeResultsSummaries = (
  currentPolicyPeriodId: number,
  previousPolicyPeriodId?: number,
  search?: string,
  sortField?: string,
  sortDirection?: string,
  sortConceptId?: number
) => {
  const sanitizedSearch = search?.trim() || null

  const {
    data: currentPeriodData,
    error: currentPeriodError,
    refetch: currentPeriodRefetch,
    loading: currentPeriodLoading,
    fetchMore,
    networkStatus,
  } = useQuery(GetEmployeeResultsSummariesDocument, {
    variables: {
      policyPeriodId: currentPolicyPeriodId,
      search: sanitizedSearch,
      sortField: sortField || 'name',
      sortConceptId: sortConceptId,
      sortDirection: sortDirection || 'asc',
      first: PAGINATION_PER_PAGE,
      offsetPage: INITIAL_PAGE,
    },
    fetchPolicy: 'network-only',
  })

  const currentPeriodResultsSummaries =
    currentPeriodData?.payroll.employeeResultsSummariesConnection.nodes ?? []

  const employeeIds: number[] = currentPeriodResultsSummaries.map((summary) => summary.employee.id)

  const {
    data: previousPeriodData,
    error: previousPeriodError,
    loading: previousPeriodLoading,
  } = useQuery(GetEmployeeResultsSummariesDocument, {
    variables: {
      policyPeriodId: previousPolicyPeriodId!,
      employeeIds,
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-first',
    skip: !previousPolicyPeriodId || employeeIds.length === 0,
  })

  const previousPeriodResultSummaries =
    previousPeriodData?.payroll.employeeResultsSummariesConnection.nodes ?? []

  const { loading: loadingConcepts, concepts: mappedConcepts } = useConcepts(currentPolicyPeriodId)

  const { resultDispatch } = usePayrollResults()

  useEffect(() => {
    if (!currentPeriodLoading) {
      resultDispatch({
        type: UPDATE_DATA,
        payload: {
          sortedResultColumns: configureResultTableColumns(mappedConcepts),
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPeriodLoading])

  return {
    refetch: currentPeriodRefetch,
    fetchMore,
    fetching: currentPeriodLoading || previousPeriodLoading || loadingConcepts,
    currentPeriodResultsSummaries: currentPeriodResultsSummaries,
    previousPeriodResultSummaries,
    mappedResultConcepts: mappedConcepts,
    error: currentPeriodError || previousPeriodError,
    hasNextPage:
      currentPeriodData?.payroll.employeeResultsSummariesConnection.pageInfo.hasNextPage ?? false,
    totalCount: currentPeriodData?.payroll.employeeResultsSummariesConnection.totalCount ?? 0,
    loadingMore: networkStatus === NetworkStatus.fetchMore,
  }
}

const configureResultTableColumns = (
  columns: PayrollResultConceptItem['concept'][]
): PayrollResultConceptItem['concept'][] => {
  const localStorageData = localStorage.getItem(LOCAL_STORAGE_KEYS.payrollResultsTableColumnConfig)

  let payrollResultColumnConfig: PayrollResultColumnConfig[] = []

  try {
    payrollResultColumnConfig = localStorageData ? JSON.parse(localStorageData) : []
  } catch (error) {
    console.error('Error parsing localStorageData:', error) // eslint-disable-line no-console
  }

  if (payrollResultColumnConfig) {
    if (columns.length > 0) {
      // Remove all configs that are not in the columns
      const prunedConfigs = pruneConfigs(payrollResultColumnConfig, columns)

      // Map new columns not present in the config to config
      const updatedConfigs = updateConfigWithNewColumns(prunedConfigs, columns)

      localStorage.setItem(
        LOCAL_STORAGE_KEYS.payrollResultsTableColumnConfig,
        JSON.stringify(updatedConfigs)
      )

      // Show only visible columns in the order defined by the config
      return updatedConfigs
        .filter((config) => config.isVisible)
        .map((config) => columns.find((column) => column.id === config.id)!)
    }

    return columns
  }

  payrollResultColumnConfig = columns.map((column) => ({
    id: column.id,
    name: column.name,
    label: column.label,
    isVisible: true,
    default: column.default,
  }))

  localStorage.setItem(
    LOCAL_STORAGE_KEYS.payrollResultsTableColumnConfig,
    JSON.stringify(payrollResultColumnConfig)
  )

  return columns
}

const pruneConfigs = (
  configs: PayrollResultColumnConfig[],
  columns: PayrollResultConceptItem['concept'][]
) => {
  return configs.filter((config) => columns.find((column) => config.id === column.id))
}

const updateConfigWithNewColumns = (
  configs: PayrollResultColumnConfig[],
  columns: PayrollResultConceptItem['concept'][]
) => {
  const existingConfigIds = new Set(configs.map((config) => config.id))
  const prunedConfigs = configs

  for (const column of columns) {
    if (!existingConfigIds.has(column.id)) {
      configs.push({
        id: column.id,
        name: column.name,
        label: column.label,
        isVisible: true,
        default: column.default,
      })
    }
  }

  return prunedConfigs
}
